import { Injectable } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  CanMatch,
  Route,
  Router,
  RouterStateSnapshot,
  UrlSegment
} from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import { DEFAULT_URL } from '@app/app.constant';
import { AuthenticationService } from '@core/services/auth.service';
import { LoaderService } from '@core/services/loader.service';
import { EAccess } from './access.model';

@Injectable({ providedIn: 'root' })
export class AccessGuard implements CanMatch {
  constructor(
    private router: Router,
    private loader: LoaderService,
    private auth: AuthenticationService
  ) {}

  /** Controllers the user is allowed to access (set elsewhere). */
  public static accesss: EAccess[] = [];
  /** Map of controller -> permitted method names. */
  public static accessMethods: { [key in EAccess]?: string[] } = {};

  public static hasAccess(controllerName: EAccess): boolean {
    return !controllerName || AccessGuard.accesss.includes(controllerName);
  }

  public static hasMethodAccess(
    controllerName: EAccess,
    controllerMethodName: string
  ): boolean {
    if (!controllerName || !controllerMethodName) return true;
    const methods = AccessGuard.accessMethods[controllerName];
    return !!methods?.includes(controllerMethodName);
  }

  public static hasAnyOneMethodAccess(
    accessArray: { controllerName: EAccess; controllerMethodName: string }[]
  ): boolean {
    return accessArray.some((a) => {
      if (!a.controllerName) return true;
      const m = AccessGuard.accessMethods[a.controllerName];
      return !!m?.includes(a.controllerMethodName);
    });
  }

  canMatch(route: Route, _segments: UrlSegment[]): Observable<boolean> {
    const data = route.data || {};
    return this.evaluate(
      data['access'] as EAccess | undefined,
      data['accessMethod'] as string | undefined,
      data['accessControllers'] as EAccess[] | undefined,
      !!data['showLoader'],
      data['featureFlag'] as string | undefined,
      data['hiddenWhenFlag'] as string | undefined
    );
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot
  ): Observable<boolean> {
    const { access, accessMethod, accessControllers, showLoader, featureFlag, hiddenWhenFlag } =
      route.data || {};
    return this.evaluate(access, accessMethod, accessControllers, !!showLoader, featureFlag, hiddenWhenFlag);
  }

  canActivateChild(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> {
    return this.canActivate(route, state);
  }

  private evaluate(
    controllerName: EAccess | undefined,
    controllerMethodName: string | undefined,
    controllersForItem: EAccess[] | undefined,
    showLoader: boolean,
    featureFlag?: string,
    hiddenWhenFlag?: string
  ): Observable<boolean> {
    // The inverse of featureFlag: a page another module supersedes (the project-wide Objectives
    // list while the Support Plan module is on) is refused when the named user flag is set.
    if (hiddenWhenFlag && (this.auth.currentUser as any)?.[hiddenWhenFlag]) {
      if (showLoader) this.loader.hide();
      this.router.navigate([DEFAULT_URL]);
      return of(false);
    }
    if (featureFlag) {
      // '|' = any-of: a route shared by multiple modules (e.g. the triggers config page,
      // used by both Risk Alerts and Workflow Events) passes when ANY listed flag is on.
      const user = this.auth.currentUser as any;
      const flags = featureFlag.split('|');
      if (!user || !flags.some((f) => user[f])) {
        if (showLoader) this.loader.hide();
        this.router.navigate([DEFAULT_URL]);
        return of(false);
      }
    }
    if (
      !controllerName ||
      (AccessGuard.hasAccess(controllerName) &&
        AccessGuard.hasMethodAccess(controllerName, controllerMethodName || ''))
    ) {
      const missing = (controllersForItem ?? []).filter(
        (c) => !AccessGuard.accessMethods[c]?.length
      );

      const shouldShowLoader = showLoader && missing.length > 0;
      if (shouldShowLoader) this.loader.show();

      return this.fetchMethods(missing).pipe(
        finalize(() => {
          if (shouldShowLoader) this.loader.hide();
        })
      );
    } else {
      if (showLoader) this.loader.hide();
      this.router.navigate([DEFAULT_URL]);
      return of(false);
    }
  }

  private fetchMethods(controllers: EAccess[]): Observable<boolean> {
    if (!controllers?.length) return of(true);
    return this.auth.getPermitedMultiControllerMethodsCached(controllers).pipe(
      map((res) => {
        res.forEach((item) => {
          AccessGuard.accessMethods[item.controllerName as EAccess] =
            item.permitedMethods || [];
        });
        return true;
      }),
      catchError(() => of(true))
    );
  }
}
